<template>
  <main class="checkout-crypto-page" :style="pageStyle">
    <section class="checkout-hero" :style="heroStyle">
      <div class="checkout-hero__shade"></div>
      <div class="checkout-hero__content">
        <img v-if="visuals.logo" class="checkout-hero__logo" :src="visuals.logo" :alt="visuals.name">
        <div>
          <div class="checkout-hero__label">{{ $t('checkout.donation_label') }}</div>
          <h1>{{ visuals.name }}</h1>
        </div>
      </div>
    </section>

    <section class="checkout-crypto-body">
      <div class="checkout-crypto-panel">
        <header class="checkout-crypto-header">
          <h2>{{ $t('checkout.crypto.transfer_title', {currency: currencySymbol}) }}</h2>
          <p>{{ $t('checkout.crypto.description') }}</p>
        </header>

        <div class="checkout-crypto-grid">
          <div>
            <button type="button" class="checkout-crypto-field" @click="copyAmount">
              <span>{{ $t('checkout.crypto.amount') }}</span>
              <strong>{{ amount }}</strong>
              <small v-if="amountCopied">{{ $t('checkout.crypto.amount_copied') }}</small>
            </button>

            <button type="button" class="checkout-crypto-field" @click="copyAddress">
              <span>{{ $t('checkout.crypto.address') }}</span>
              <strong>{{ address }}</strong>
              <small v-if="addressCopied">{{ $t('checkout.crypto.address_copied') }}</small>
            </button>
          </div>

          <div class="checkout-crypto-side">
            <img v-if="qrCode" class="checkout-crypto-qr" :src="qrCode" :alt="$t('checkout.crypto.qr_code')">
            <div v-if="paymentURL" class="checkout-crypto-wallet">
              <p>{{ $t('checkout.crypto.wallet_hint', {currency: currencySymbol}) }}</p>
              <a class="checkout-crypto-wallet-link" :href="paymentURL">{{ $t('checkout.crypto.pay_in_app') }}</a>
            </div>
          </div>
        </div>

        <div class="checkout-crypto-footer">
          <div class="checkout-crypto-actions">
            <a href="/checkout" class="checkout-crypto-back">{{ $t('checkout.subscription.back') }}</a>
          </div>
          <div class="checkout-crypto-status">
            <span>{{ statusLabel }}</span>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import {logError, logWarn} from '@/helpers';

export default {
  name: 'CheckoutCryptoPayment',
  data() {
    return {
      amountCopied: false,
      addressCopied: false,
      copyTimers: [],
    };
  },
  computed: {
    visuals() {
      return this.$store.getters.visuals || {};
    },
    pageStyle() {
      if (!this.visuals.controlColor) return {};
      return {'--control-color': this.visuals.controlColor};
    },
    heroStyle() {
      if (!this.visuals.coverImage) return {};
      return {backgroundImage: `url("${this.visuals.coverImage}")`};
    },
    currencySymbol() {
      return this.$store.getters.selectedCryptoCurrencySymbol || '';
    },
    amount() {
      return this.$store.getters.amount;
    },
    address() {
      return this.$store.getters.paymentAddress;
    },
    qrCode() {
      return this.$store.getters.paymentQRCode;
    },
    paymentURL() {
      return this.$store.getters.paymentURL;
    },
    statusLabel() {
      const mode = this.$store.getters.cryptocurrencyPaymentMode;
      if (mode === 'awaiting_confirmation') {
        return this.$t('checkout.crypto.awaiting_confirmation');
      }
      return this.$t('checkout.crypto.awaiting_payment');
    },
    hasCryptoPaymentInProgress() {
      return !!(this.$store.getters.cryptocurrencyPaymentMode && this.address);
    },
  },
  created() {
    this.ensurePaymentContext();
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.ensurePaymentContext();
    });
  },
  beforeRouteUpdate(to, from, next) {
    this.ensurePaymentContext(next);
  },
  methods: {
    ensurePaymentContext(next) {
      if (!this.hasCryptoPaymentInProgress) {
        if (next) {
          next('/checkout');
          return;
        }
        this.$router.replace('/checkout');
        return;
      }
      if (next) {
        next();
      }
    },
    copyAmount() {
      this.copyToClipboard(this.amount, 'amountCopied');
    },
    copyAddress() {
      this.copyToClipboard(this.address, 'addressCopied');
    },
    copyToClipboard(value, flag) {
      if (!value) {
        return;
      }
      const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : null;
      if (clipboard && typeof clipboard.writeText === 'function') {
        clipboard.writeText(value).catch((err) => {
          logWarn('Clipboard API copy failed, falling back to execCommand', err);
          this.fallbackCopy(value);
        });
      } else {
        this.fallbackCopy(value);
      }
      this[flag] = true;
      const timer = setTimeout(() => {
        this[flag] = false;
      }, 2000);
      this.copyTimers.push(timer);
    },
    fallbackCopy(value) {
      const input = document.createElement('input');
      input.value = value;
      input.setAttribute('readonly', 'readonly');
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      try {
        document.execCommand('copy');
      } catch (e) {
        logError('Fallback execCommand copy failed', e);
      }
      document.body.removeChild(input);
    },
  },
  beforeDestroy() {
    this.copyTimers.forEach(clearTimeout);
  },
};
</script>

<style scoped>
.checkout-crypto-page {
  min-height: 100vh;
  background: #f5f7f8;
  color: #17212b;
}
.checkout-hero {
  position: relative;
  min-height: 260px;
  background: #204f62;
  background-position: center;
  background-size: cover;
}
.checkout-hero__shade {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(11, 31, 42, .82), rgba(11, 31, 42, .38));
}
.checkout-hero__content {
  position: relative;
  z-index: 1;
  max-width: 1040px;
  margin: 0 auto;
  min-height: 260px;
  padding: 32px 20px;
  display: flex;
  align-items: center;
  gap: 18px;
  color: #fff;
}
.checkout-hero__logo {
  width: 78px;
  height: 78px;
  object-fit: cover;
  border-radius: 8px;
  background: #fff;
}
.checkout-hero__label {
  font-size: 14px;
  opacity: .82;
}
.checkout-hero h1 {
  margin: 4px 0 0;
  font-size: 42px;
  line-height: 1.1;
  letter-spacing: 0;
}
.checkout-crypto-body {
  max-width: 1040px;
  margin: -42px auto 0;
  padding: 0 20px 56px;
  position: relative;
  z-index: 2;
}
.checkout-crypto-panel {
  background: #fff;
  border: 1px solid #dde3e8;
  border-radius: 8px;
  box-shadow: 0 16px 40px rgba(25, 39, 52, .08);
  padding: 28px;
}
.checkout-crypto-header h2 {
  margin: 0 0 10px;
  font-size: 26px;
  letter-spacing: 0;
}
.checkout-crypto-header p {
  margin: 0;
  color: #4c5a66;
  line-height: 1.55;
}
.checkout-crypto-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 24px;
  margin-top: 24px;
  align-items: start;
}
.checkout-crypto-field {
  display: block;
  width: 100%;
  text-align: left;
  border: 1px solid #cfd8df;
  border-radius: 8px;
  background: #fff;
  padding: 14px;
  cursor: pointer;
}
.checkout-crypto-field + .checkout-crypto-field {
  margin-top: 12px;
}
.checkout-crypto-field:hover {
  border-color: var(--control-color, #1f6feb);
}
.checkout-crypto-field span,
.checkout-crypto-field small {
  display: block;
  color: #66737f;
  font-size: 13px;
}
.checkout-crypto-field strong {
  display: block;
  margin-top: 6px;
  font-size: 18px;
  line-height: 1.35;
  word-break: break-all;
  font-weight: 600;
  color: #17212b;
}
.checkout-crypto-field small {
  margin-top: 8px;
  color: var(--control-color, #1f6feb);
}
.checkout-crypto-side {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.checkout-crypto-qr {
  width: 100%;
  max-width: 300px;
  border-radius: 8px;
  border: 1px solid #dde3e8;
  background: #fff;
}
.checkout-crypto-wallet {
  border: 1px solid #dde3e8;
  border-radius: 8px;
  padding: 14px;
}
.checkout-crypto-wallet p {
  margin: 0 0 12px;
  color: #4c5a66;
}
.checkout-crypto-wallet-link {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  border-radius: 6px;
  background: var(--control-color, #16794c);
  color: #fff;
  text-decoration: none;
  font-weight: 700;
}
.checkout-crypto-footer {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-top: 22px;
}
.checkout-crypto-status span {
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  border-radius: 999px;
  padding: 0 14px;
  border: 1px solid var(--control-color, #1f6feb);
  background: transparent;
  color: var(--control-color, #1f6feb);
  font-weight: 600;
}
.checkout-crypto-back {
  color: var(--control-color, #1f6feb);
  text-decoration: none;
  font-weight: 600;
}
.checkout-crypto-back:hover {
  text-decoration: underline;
}
@media (max-width: 820px) {
  .checkout-hero {
    min-height: 220px;
  }
  .checkout-hero__content {
    align-items: center;
    min-height: 220px;
    padding: 28px 16px;
  }
  .checkout-hero__logo {
    width: 64px;
    height: 64px;
  }
  .checkout-hero h1 {
    font-size: 32px;
  }
  .checkout-crypto-body {
    margin: 16px auto 0;
    padding: 0 16px 40px;
  }
  .checkout-crypto-panel {
    padding: 20px;
  }
  .checkout-crypto-grid {
    grid-template-columns: 1fr;
  }
  .checkout-crypto-footer {
    align-items: center;
    flex-direction: column;
    justify-content: center;
    text-align: center;
  }
  .checkout-crypto-status {
    order: 1;
  }
  .checkout-crypto-actions {
    order: 2;
  }
}
@media (max-width: 480px) {
  .checkout-hero__content {
    gap: 14px;
  }
  .checkout-hero h1 {
    font-size: 28px;
  }
  .checkout-crypto-header h2 {
    font-size: 24px;
  }
}
</style>
